import { useTranslation } from 'react-i18next'

import {
  TableBody,
  TableCell,
  TableColumnHeader,
  TableFooter,
  TableHeader,
  TableRoot,
  TableRow,
} from '@enechain/ecloud-designsystem'

import type {
  ExecutionAreaSummaryRow,
  ExecutionSummaryTotal,
} from '~/components/bidding/common/executionSummary'
import { TABLE_HEADER_BG } from '~/components/common/MasterDataTable'
import { TableMessageRow } from '~/components/common/TableMessageRow'
import { TradeSideLabel } from '~/components/common/TradeSideLabel'
import { convertAreaLabel } from '~/models/area'
import { formatPctSum } from '~/models/pctDisplay'

type Props = {
  rows: ExecutionAreaSummaryRow[]
  total: ExecutionSummaryTotal
  /**
   * 合計行の約定率を `formatPctSum` (`docs/specs/conventions.md`「数値表示」) で出す。
   * 既定 (false) は従来の小数 1 桁・ゼロ除算「—」 (スポットが使う)。 個別行は変えない。
   * スポットも同じ表記に移る時に、 この prop と小数 1 桁の分岐をまとめて消す。
   */
  totalFillRateByConventions?: boolean
}

const DASH = '—'

/**
 * 列ヘッダを器の上端に貼り付ける props。
 *
 * `thead` / `tfoot` という row group 自身への sticky はブラウザ差があるため、
 * ヘッダも合計行も**セル**に張る (合計行は {@link STICKY_TOTAL_CELL_PROPS})。
 *
 * 地色は `MasterDataTable` の single source から引く (sticky セルは地色が無いと
 * スクロールしてきた行が透ける)。 `TABLE_HEADER_CELL_STYLE` 全体は採らない —
 * `whiteSpace: nowrap` を含んでおり、 7 列を折り返して狭いカードに収めている
 * 本表では横スクロールが出てしまうため。
 */
const STICKY_HEADER_CELL_PROPS = {
  position: 'sticky',
  zIndex: 1,
  top: 0,
  backgroundColor: TABLE_HEADER_BG,
} as const

/**
 * 合計行を器の下端に貼り付ける props (ヘッダと当て方を揃えてセルに張る)。
 *
 * 重なり順はヘッダ (`zIndex: 1`) より上。 合計行は最下端まで残り、 スクロール
 * してきた行と最後まで重なるため。
 */
const STICKY_TOTAL_CELL_PROPS = {
  position: 'sticky',
  zIndex: 2,
  bottom: 0,
  backgroundColor: 'Surface.primary',
} as const

const formatMw = (value: number): string => value.toFixed(1)
const formatPct = (value: number | undefined): string =>
  value === undefined ? DASH : value.toFixed(1)
const formatPrice = (value: number | undefined): string =>
  value === undefined ? DASH : `¥${value.toFixed(2)}`

/** 列の数 (空の時の 1 行メッセージの colSpan)。 */
const COLUMN_COUNT = 7

/**
 * 約定結果「エリア × 売買 別 集計」 テーブル (7 列 + 合計行)。
 *
 * spec: `docs/specs/bidding/spot-bidding.md` AC-BSP-002b /
 *       `docs/specs/bidding/intraday-bidding.md` AC-BIN-022b
 *       (= 「同一列構成の共通コンポーネントとして実装する」 と spec が明記)
 * design: `design/js/exec-summary.js` (プロト側の共通コンポーネント) /
 *         `design/bidding/spot.html` `#spot-exec-summary-table`
 *
 * **収支の列は持たない** (spec AC-BSP-090 / AC-BIN-079。 手数料が固定か従量かは事業者の契約で
 * 違い、 手数料単価 1 本の式では正しい収支にならないため)。 8080 も同じ列を外している
 * (`js/exec-summary.js`、 enechain/ebalance#10148)。
 *
 * 集計値は呼び出し側が `computeExecutionAreaSummary` で作って渡す (= 本 component は
 * 表示専用)。 市場ごとに違うのは「札 → record の写像」 だけ。
 *
 * **スクロール器は持たない**。 列ヘッダと合計行に sticky を宣言するだけで、 貼り付く
 * 基準になる器 (= `overflow` を持つ祖先) は呼び出し側が用意する。 器を component 側に
 * 置くと「親が flex column で自分は残り高さを取る子」 という呼び出し側の layout 前提を
 * 型に出ないまま抱え込むため、 所有者を呼び出し側 1 箇所に寄せている (PR #6679 レビュー)。
 * 器で包まない呼び出し側では、 行は内容高さのまま描画されて sticky は効かない。
 */
export const ExecutionAreaSummaryTable = ({
  rows,
  total,
  totalFillRateByConventions = false,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  return (
    <TableRoot fontSize="md" size="sm">
      <TableHeader>
        <TableRow>
          <TableColumnHeader {...STICKY_HEADER_CELL_PROPS}>
            {t('common:executionSummary.colArea')}
          </TableColumnHeader>
          <TableColumnHeader {...STICKY_HEADER_CELL_PROPS} textAlign="center">
            {t('common:executionSummary.colSide')}
          </TableColumnHeader>
          <TableColumnHeader {...STICKY_HEADER_CELL_PROPS} textAlign="end">
            {t('common:executionSummary.colBidCount')}
          </TableColumnHeader>
          <TableColumnHeader {...STICKY_HEADER_CELL_PROPS} textAlign="end">
            {t('common:executionSummary.colBidMw')}
          </TableColumnHeader>
          <TableColumnHeader {...STICKY_HEADER_CELL_PROPS} textAlign="end">
            {t('common:executionSummary.colExecMw')}
          </TableColumnHeader>
          <TableColumnHeader {...STICKY_HEADER_CELL_PROPS} textAlign="end">
            {t('common:executionSummary.colFillRate')}
          </TableColumnHeader>
          <TableColumnHeader {...STICKY_HEADER_CELL_PROPS} textAlign="end">
            {t('common:executionSummary.colAvgPrice')}
          </TableColumnHeader>
        </TableRow>
      </TableHeader>
      <TableBody>
        {/* 0 件は表の空行 (UI 辞書 components/table.md §3: 1 行・高さ 88px 以上・
            `Text.midEmphasis`・下罫線なし) */}
        {rows.length === 0 && (
          <TableMessageRow
            colSpan={COLUMN_COUNT}
            message={t('common:executionSummary.empty')}
          />
        )}
        {rows.map((row, index) => (
          <TableRow
            key={`${row.areaCode}-${row.side}`}
            backgroundColor={index % 2 === 1 ? 'Surface.secondary' : undefined}
          >
            <TableCell whiteSpace="nowrap">
              {convertAreaLabel(row.areaCode)}
            </TableCell>
            <TableCell textAlign="center">
              <TradeSideLabel side={row.side} />
            </TableCell>
            <TableCell textAlign="end" fontVariantNumeric="tabular-nums">
              {row.bidCount}
            </TableCell>
            <TableCell textAlign="end" fontVariantNumeric="tabular-nums">
              {formatMw(row.bidVolumeMw)}
            </TableCell>
            <TableCell textAlign="end" fontVariantNumeric="tabular-nums">
              {formatMw(row.execVolumeMw)}
            </TableCell>
            <TableCell textAlign="end" fontVariantNumeric="tabular-nums">
              {formatPct(row.fillRatePct)}
            </TableCell>
            <TableCell textAlign="end" fontVariantNumeric="tabular-nums">
              {formatPrice(row.weightedAvgPriceYenPerKwh)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
      {rows.length > 0 && (
        <TableFooter>
          <TableRow>
            <TableCell {...STICKY_TOTAL_CELL_PROPS} fontWeight="semibold">
              {t('common:executionSummary.rowTotal')}
            </TableCell>
            <TableCell {...STICKY_TOTAL_CELL_PROPS} textAlign="center">
              {DASH}
            </TableCell>
            <TableCell
              {...STICKY_TOTAL_CELL_PROPS}
              fontWeight="semibold"
              textAlign="end"
              fontVariantNumeric="tabular-nums"
            >
              {total.bidCount}
            </TableCell>
            <TableCell
              {...STICKY_TOTAL_CELL_PROPS}
              fontWeight="semibold"
              textAlign="end"
              fontVariantNumeric="tabular-nums"
            >
              {formatMw(total.bidVolumeMw)}
            </TableCell>
            <TableCell
              {...STICKY_TOTAL_CELL_PROPS}
              fontWeight="semibold"
              textAlign="end"
              fontVariantNumeric="tabular-nums"
            >
              {formatMw(total.execVolumeMw)}
            </TableCell>
            <TableCell
              {...STICKY_TOTAL_CELL_PROPS}
              fontWeight="semibold"
              textAlign="end"
              fontVariantNumeric="tabular-nums"
            >
              {totalFillRateByConventions
                ? formatPctSum(total.fillRatePct)
                : formatPct(total.fillRatePct)}
            </TableCell>
            <TableCell
              {...STICKY_TOTAL_CELL_PROPS}
              fontWeight="semibold"
              textAlign="end"
              fontVariantNumeric="tabular-nums"
            >
              {formatPrice(total.weightedAvgPriceYenPerKwh)}
            </TableCell>
          </TableRow>
        </TableFooter>
      )}
    </TableRoot>
  )
}
